import React, { useState } from 'react';
import { FIREBASE_DB, FIREBASE_AUTH } from '../firebase.config';
import { collection, addDoc } from 'firebase/firestore';

const ReferralProgram = () => {
  const [formData, setFormData] = useState({
    employeeName: '',
    employeeEmail: '',
    employeePhone: '',
    candidateName: '',
    candidateEmail: '',
    candidatePhone: '',
    positionReferredFor: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = FIREBASE_AUTH.currentUser;
      if (!user) {
        alert('You must be logged in to submit a referral');
        return;
      }

      await addDoc(collection(FIREBASE_DB, 'referrals'), {
        ...formData,
        employeeId: user.uid,
        submissionDate: new Date().toISOString(),
        status: 'pending'
      });

      alert('Referral submitted successfully!');
      setFormData({
        employeeName: '',
        employeeEmail: '',
        employeePhone: '',
        candidateName: '',
        candidateEmail: '',
        candidatePhone: '',
        positionReferredFor: '',
      });
    } catch (error) {
      console.error('Error submitting referral:', error);
      alert('Error submitting referral. Please try again.');
    }
  };

  const referralAwards = [
    { position: 'Administration', amount: 75 },
    { position: 'Customer Service', amount: 50 },
    { position: 'Engineering', amount: 100 },
    { position: 'Finance & Accounting', amount: 75 },
    { position: 'Information Technology', amount: 100 },
    { position: 'Light Industrial', amount: 25 },
    { position: 'Project Manager', amount: 100 },
    { position: 'Sales & Marketing', amount: 75 },
  ];

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Program Overview Section */}
        <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-5 border-b border-gray-200">
            <h1 className="text-2xl font-semibold text-gray-900">Employee Referral Program</h1>
          </div>
          <div className="p-6">
            <div className="prose max-w-none">
              <h2 className="text-xl font-semibold mb-4">PROGRAM OVERVIEW:</h2>
              <p className="mb-6">
                We're excited to introduce our Employee Referral Program! This is your chance to not
                only help your friends and connections land great jobs but also to be rewarded for it! By
                tapping into your networks, we can attract top talent who reflect our company values
                and align with the talent we seek to hire.
              </p>

              <h2 className="text-xl font-semibold mb-4">ELIGIBILITY:</h2>
              <p className="mb-6">Every employee is eligible to participate in the referral program.</p>

              <h2 className="text-xl font-semibold mb-4">HOW IT WORKS:</h2>
              <h3 className="text-lg font-medium mb-2">Referral Process:</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Employees can refer candidates by submitting their information through our designated referral platform.</li>
                <li>Referrals must include the candidate's resume and a brief note about why the employee believes the candidate would be a good fit for a job opportunity.</li>
              </ul>

              <h3 className="text-lg font-medium mb-2">Candidate Review:</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>All referred candidates will be reviewed by the SBG recruiting team and may undergo standard screening processes.</li>
                <li>Employees will be notified if their referral has progressed to an interview.</li>
              </ul>

              <h2 className="text-xl font-semibold mb-4">Referral Award:</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vacant Position Level
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Award Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {referralAwards.map((award, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {award.position}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${award.amount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h2 className="text-xl font-semibold mt-8 mb-4">PROGRAM GUIDELINES:</h2>
              <ul className="list-disc pl-6 mb-6">
                <li>There is no limit to the number of referrals an employee can make.</li>
                <li>Referrals must be for specific job openings listed on our employee website portal.</li>
                <li>The referring employee must agree to have his or her name used when the company contacts the candidate.</li>
                <li>The first employee to refer a candidate will be the only referring employee eligible for payment.</li>
                <li>Only candidates who meet the essential qualifications for the position will be considered.</li>
                <li>All candidates will be evaluated for employment consistent with company policies and procedures.</li>
                <li>All information regarding the hiring decision will remain strictly confidential.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Referral Form Section */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Employee Referral Form</h2>
          </div>
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Employee Information</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="employeeName" className="block text-sm font-medium text-gray-700">
                      Employee Name
                    </label>
                    <input
                      type="text"
                      name="employeeName"
                      id="employeeName"
                      required
                      value={formData.employeeName}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label htmlFor="employeeEmail" className="block text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="employeeEmail"
                      id="employeeEmail"
                      required
                      value={formData.employeeEmail}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label htmlFor="employeePhone" className="block text-sm font-medium text-gray-700">
                      Phone No
                    </label>
                    <input
                      type="tel"
                      name="employeePhone"
                      id="employeePhone"
                      required
                      value={formData.employeePhone}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Referral Information</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="candidateName" className="block text-sm font-medium text-gray-700">
                      Candidate Name
                    </label>
                    <input
                      type="text"
                      name="candidateName"
                      id="candidateName"
                      required
                      value={formData.candidateName}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label htmlFor="candidateEmail" className="block text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="candidateEmail"
                      id="candidateEmail"
                      required
                      value={formData.candidateEmail}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label htmlFor="candidatePhone" className="block text-sm font-medium text-gray-700">
                      Phone No
                    </label>
                    <input
                      type="tel"
                      name="candidatePhone"
                      id="candidatePhone"
                      required
                      value={formData.candidatePhone}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label htmlFor="positionReferredFor" className="block text-sm font-medium text-gray-700">
                      Position Referred For
                    </label>
                    <select
                      name="positionReferredFor"
                      id="positionReferredFor"
                      required
                      value={formData.positionReferredFor}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    >
                      <option value="">Select a position</option>
                      {referralAwards.map((award, index) => (
                        <option key={index} value={award.position}>
                          {award.position}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                >
                  Submit Referral
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralProgram; 